import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ReferenceLine, ResponsiveContainer, Area, ComposedChart 
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Trash2, TrendingUp, TrendingDown, Calculator, RefreshCw, 
  BarChart3, Info, DollarSign, Target, AlertTriangle, Clock, CandlestickChart
} from 'lucide-react';
import { toast } from 'sonner';
import TradingChartModal from './TradingChartModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strategy templates
const STRATEGY_TEMPLATES = {
  long_call: { name: "Long Call", legs: [{ option_type: "call", position_type: "buy", strikeOffset: 0 }] },
  long_put: { name: "Long Put", legs: [{ option_type: "put", position_type: "buy", strikeOffset: 0 }] },
  covered_call: { name: "Covered Call", legs: [{ option_type: "call", position_type: "sell", strikeOffset: 5 }] },
  bull_call_spread: { 
    name: "Bull Call Spread", 
    legs: [
      { option_type: "call", position_type: "buy", strikeOffset: 0 },
      { option_type: "call", position_type: "sell", strikeOffset: 10 }
    ]
  },
  bear_put_spread: {
    name: "Bear Put Spread",
    legs: [
      { option_type: "put", position_type: "buy", strikeOffset: 0 },
      { option_type: "put", position_type: "sell", strikeOffset: -10 }
    ]
  },
  iron_condor: {
    name: "Iron Condor",
    legs: [
      { option_type: "put", position_type: "buy", strikeOffset: -20 },
      { option_type: "put", position_type: "sell", strikeOffset: -10 },
      { option_type: "call", position_type: "sell", strikeOffset: 10 },
      { option_type: "call", position_type: "buy", strikeOffset: 20 }
    ]
  },
  straddle: {
    name: "Straddle",
    legs: [
      { option_type: "call", position_type: "buy", strikeOffset: 0 },
      { option_type: "put", position_type: "buy", strikeOffset: 0 }
    ]
  },
  strangle: {
    name: "Strangle",
    legs: [
      { option_type: "call", position_type: "buy", strikeOffset: 10 },
      { option_type: "put", position_type: "buy", strikeOffset: -10 }
    ]
  }
};

const PLChart = ({ initialSymbol = '', initialLegs = [], currentStockPrice = null }) => {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [stockPrice, setStockPrice] = useState(currentStockPrice);
  const [legs, setLegs] = useState(initialLegs.length > 0 ? initialLegs : [
    { id: 1, strike: '', option_type: 'call', position_type: 'buy', premium: '', quantity: 1, loading: false }
  ]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expirations, setExpirations] = useState([]);
  const [selectedExpiration, setSelectedExpiration] = useState('');
  const [daysToExpiration, setDaysToExpiration] = useState(30);
  const [showTimeDdecay, setShowTimeDecay] = useState(false);
  const [activeChartView, setActiveChartView] = useState('expiration');
  
  // Chart modal state
  const [isChartOpen, setIsChartOpen] = useState(false);

  // Update state when props change (from portfolio position selection)
  useEffect(() => {
    if (initialSymbol && initialSymbol !== symbol) {
      setSymbol(initialSymbol);
    }
  }, [initialSymbol]);

  useEffect(() => {
    if (currentStockPrice && currentStockPrice !== stockPrice) {
      setStockPrice(currentStockPrice);
    }
  }, [currentStockPrice]);

  useEffect(() => {
    if (initialLegs && initialLegs.length > 0) {
      // Convert portfolio leg format to PLChart format
      const convertedLegs = initialLegs.map((leg, idx) => ({
        id: idx + 1,
        strike: leg.strike?.toString() || '',
        option_type: leg.optionType || leg.option_type || 'call',
        position_type: leg.positionType === 'long' ? 'buy' : (leg.positionType === 'short' ? 'sell' : (leg.position_type || 'buy')),
        premium: leg.premium?.toString() || '',
        quantity: leg.quantity || 1,
        loading: false
      }));
      setLegs(convertedLegs);
      
      // Set expiration from first leg if available
      const expiration = initialLegs[0]?.expiration || initialLegs[0]?.expiration_date;
      if (expiration) {
        const expDate = new Date(expiration);
        const formattedExp = expDate.toISOString().split('T')[0];
        setSelectedExpiration(formattedExp);
        
        // Calculate days to expiration
        const today = new Date();
        const diffDays = Math.ceil(Math.abs(expDate - today) / (1000 * 60 * 60 * 24));
        setDaysToExpiration(diffDays);
      }
      
      // Auto-calculate chart after loading
      if (initialSymbol) {
        toast.success(`Loaded ${initialLegs.length} leg(s) for ${initialSymbol}`);
      }
    }
  }, [initialLegs, initialSymbol]);

  // Fetch stock price and expirations when symbol changes
  const fetchStockData = useCallback(async () => {
    if (!symbol.trim()) return;
    
    setLoading(true);
    toast.info(`Loading ${symbol.toUpperCase()} data...`);
    
    try {
      // Use the fast data endpoints with timeout
      const [priceRes, expirationsRes] = await Promise.all([
        axios.get(`${API}/data/price/${symbol.toUpperCase()}`, { timeout: 10000 }),
        axios.get(`${API}/data/expirations/${symbol.toUpperCase()}`, { timeout: 10000 })
      ]);
      
      const price = priceRes.data.price;
      const expList = expirationsRes.data.expirations || [];
      
      setStockPrice(price);
      setExpirations(expList);
      
      if (expList.length > 0) {
        setSelectedExpiration(expList[0]);
        
        // Calculate days to expiration
        const expDate = new Date(expList[0]);
        const today = new Date();
        const diffTime = Math.abs(expDate - today);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDaysToExpiration(diffDays);
      }
      
      // Update leg strikes to ATM if empty
      setLegs(prev => prev.map(leg => ({
        ...leg,
        strike: leg.strike || Math.round(price)
      })));
      
      toast.success(`Loaded ${symbol.toUpperCase()} @ $${price.toFixed(2)}`);
    } catch (error) {
      console.error('Error fetching stock data:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load data: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  // Auto-fetch premium for a leg using batch endpoint (single leg)
  const fetchPremium = async (legIndex) => {
    const leg = legs[legIndex];
    if (!symbol || !leg.strike || !leg.option_type || !selectedExpiration) return null;
    
    setLegs(prev => prev.map((l, i) => i === legIndex ? { ...l, loading: true } : l));
    
    try {
      const response = await axios.post(`${API}/data/premiums`, {
        symbol: symbol.toUpperCase(),
        expiration: selectedExpiration,
        legs: [{ strike: parseFloat(leg.strike), option_type: leg.option_type }]
      }, { timeout: 10000 });
      
      const fetchedLeg = response.data.legs[0];
      if (fetchedLeg && fetchedLeg.premium) {
        setLegs(prev => prev.map((l, i) => 
          i === legIndex ? { ...l, premium: fetchedLeg.premium.toFixed(2), loading: false } : l
        ));
        return fetchedLeg.premium;
      }
      
      setLegs(prev => prev.map((l, i) => i === legIndex ? { ...l, loading: false } : l));
      return null;
    } catch (error) {
      console.error('Error fetching premium:', error);
      setLegs(prev => prev.map((l, i) => i === legIndex ? { ...l, loading: false } : l));
      return null;
    }
  };

  // Fetch all premiums for legs that don't have them - using batch endpoint
  const fetchAllPremiums = async () => {
    if (!symbol || !selectedExpiration) {
      toast.error('Please select symbol and expiration first');
      return false;
    }
    
    const legsNeedingPremium = legs.filter(l => l.strike && !l.premium);
    if (legsNeedingPremium.length === 0) return true;
    
    toast.info(`Fetching premiums for ${legsNeedingPremium.length} leg(s)...`);
    
    // Mark all legs as loading
    setLegs(prev => prev.map(l => 
      l.strike && !l.premium ? { ...l, loading: true } : l
    ));
    
    try {
      // Use the fast batch premium endpoint
      const response = await axios.post(`${API}/data/premiums`, {
        symbol: symbol.toUpperCase(),
        expiration: selectedExpiration,
        legs: legsNeedingPremium.map(leg => ({
          strike: parseFloat(leg.strike),
          option_type: leg.option_type
        }))
      }, { timeout: 15000 });
      
      // Create maps of fetched premiums + per-leg status (keyed by requested strike)
      const premiumMap = {};
      const statusByKey = {};
      const snapByKey = {};
      response.data.legs.forEach(leg => {
        const key = `${leg.strike}_${leg.option_type}`;
        statusByKey[key] = leg.status;
        if (leg.nearest_listed_strike != null) snapByKey[key] = leg.nearest_listed_strike;
        if (leg.premium) premiumMap[key] = leg.premium;
      });

      // Auto-snap any strike that isn't listed to the nearest real strike, then retry once.
      const toSnap = legsNeedingPremium.filter(l => statusByKey[`${l.strike}_${l.option_type}`] === 'strike_not_listed' && snapByKey[`${l.strike}_${l.option_type}`] != null);
      if (toSnap.length > 0) {
        const snappedIds = new Set(toSnap.map(l => l.id));
        setLegs(prev => prev.map(l => snappedIds.has(l.id)
          ? { ...l, strike: snapByKey[`${l.strike}_${l.option_type}`], loading: false }
          : l));
        const names = toSnap.map(l => `${l.strike} → ${snapByKey[`${l.strike}_${l.option_type}`]}`).join(', ');
        toast.info(`Adjusted to nearest listed strike: ${names}. Fetching premiums…`);
        setTimeout(() => fetchAllPremiums(), 300);
        return true;
      }

      // Update stock price if returned
      if (response.data.current_price) {
        setStockPrice(response.data.current_price);
      }
      
      // Update all legs with fetched premiums
      setLegs(prev => prev.map((leg) => {
        const key = `${leg.strike}_${leg.option_type}`;
        if (premiumMap[key]) {
          return { ...leg, premium: premiumMap[key].toFixed(2), loading: false };
        }
        return { ...leg, loading: false };
      }));
      
      const successCount = response.data.legs.filter(l => l.found).length;
      if (successCount === legsNeedingPremium.length) {
        toast.success(`Fetched all ${successCount} premium(s)`);
        return true;
      } else {
        const unavailable = response.data.legs.filter(l => l.status === 'quote_unavailable');
        if (unavailable.length > 0) {
          const s = unavailable.map(l => `$${l.strike} ${l.option_type}`).join(', ');
          toast.warning(`No live quote for ${s} (illiquid strike). Enter a premium manually or pick another strike.`);
        } else {
          toast.warning(`Fetched ${successCount}/${legsNeedingPremium.length} premiums`);
        }
        return successCount > 0;
      }
    } catch (error) {
      console.error('Error fetching premiums:', error);
      setLegs(prev => prev.map(l => ({ ...l, loading: false })));
      const status = error.response?.status;
      let errorMsg = error.response?.data?.detail || error.message || 'Network error';
      if (status === 502) errorMsg = 'Option-chain data is temporarily unavailable. Please try again.';
      else if (status === 400) errorMsg = 'Please select a valid expiration date first.';
      toast.error(errorMsg);
      return false;
    }
  };

  // Add a new leg
  const addLeg = () => {
    const newId = Math.max(...legs.map(l => l.id), 0) + 1;
    setLegs([...legs, { 
      id: newId, 
      strike: stockPrice ? Math.round(stockPrice) : '', 
      option_type: 'call', 
      position_type: 'buy', 
      premium: '', 
      quantity: 1,
      loading: false
    }]);
  };

  // Remove a leg
  const removeLeg = (id) => {
    if (legs.length > 1) {
      setLegs(legs.filter(l => l.id !== id));
    }
  };

  // Update a leg
  const updateLeg = (id, field, value) => {
    setLegs(legs.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  // Apply strategy template
  const applyTemplate = (templateKey) => {
    const template = STRATEGY_TEMPLATES[templateKey];
    if (!template || !stockPrice) {
      toast.error('Please enter a symbol and fetch stock price first');
      return;
    }
    
    const roundedPrice = Math.round(stockPrice / 5) * 5; // Round to nearest $5
    
    const newLegs = template.legs.map((legTemplate, index) => ({
      id: index + 1,
      strike: roundedPrice + legTemplate.strikeOffset,
      option_type: legTemplate.option_type,
      position_type: legTemplate.position_type,
      premium: '',
      quantity: 1,
      loading: false
    }));
    
    setLegs(newLegs);
    toast.success(`Applied ${template.name} template`);
  };

  // Calculate P/L Chart - automatically fetches premiums if missing
  const calculateChart = async () => {
    // Validate basic inputs
    const legsWithStrikes = legs.filter(l => l.strike && l.quantity);
    if (legsWithStrikes.length === 0) {
      toast.error('Please add at least one leg with a strike price');
      return;
    }
    if (!stockPrice) {
      toast.error('Please enter a symbol and fetch the stock price');
      return;
    }
    if (!selectedExpiration) {
      toast.error('Please select an expiration date');
      return;
    }
    
    setLoading(true);
    
    try {
      // Auto-fetch premiums for legs that don't have them
      let currentLegs = [...legs];
      const legsNeedingPremium = currentLegs.filter(l => l.strike && !l.premium);
      
      if (legsNeedingPremium.length > 0) {
        toast.info(`Auto-fetching ${legsNeedingPremium.length} premium(s) from market...`);
        
        // Fetch all premiums in parallel
        const premiumPromises = currentLegs.map(async (leg, index) => {
          if (!leg.strike || leg.premium) return { index, premium: leg.premium };
          
          try {
            const response = await axios.get(`${API}/options/${symbol}/premium`, {
              params: {
                strike: leg.strike,
                option_type: leg.option_type,
                expiration: selectedExpiration
              }
            });
            return { index, premium: response.data.premium };
          } catch (error) {
            console.error(`Error fetching premium for leg ${index + 1}:`, error);
            return { index, premium: null };
          }
        });
        
        const results = await Promise.all(premiumPromises);
        
        // Update legs with fetched premiums
        currentLegs = currentLegs.map((leg, index) => {
          const result = results.find(r => r.index === index);
          if (result && result.premium !== null) {
            return { ...leg, premium: result.premium };
          }
          return leg;
        });
        
        // Update state with new premiums
        setLegs(currentLegs);
        
        const successCount = results.filter(r => r.premium !== null).length;
        toast.success(`Fetched ${legsNeedingPremium.length} premium(s) from market`);
      }
      
      // Validate we have all required data
      const validLegs = currentLegs.filter(l => l.strike && l.premium && l.quantity);
      if (validLegs.length === 0) {
        toast.error('Could not fetch premiums. Please check strike prices are valid.');
        setLoading(false);
        return;
      }
      
      // Calculate P/L chart
      const response = await axios.post(`${API}/pl-chart/calculate`, {
        symbol: symbol.toUpperCase(),
        legs: validLegs.map(l => ({
          strike: parseFloat(l.strike),
          option_type: l.option_type,
          position_type: l.position_type,
          premium: parseFloat(l.premium),
          quantity: parseInt(l.quantity)
        })),
        current_price: stockPrice,
        expiration_date: selectedExpiration,
        days_to_expiration: daysToExpiration
      });
      
      setChartData(response.data);
      toast.success('P/L chart generated!');
    } catch (error) {
      console.error('Error calculating chart:', error);
      toast.error('Failed to calculate P/L chart');
    } finally {
      setLoading(false);
    }
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const pl = payload[0].value;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-700">Stock Price: ${label}</p>
          <p className={`text-lg font-bold ${pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            P/L: {pl >= 0 ? '+' : ''}${pl.toLocaleString()}
          </p>
          {chartData?.metrics?.break_evens?.includes(parseFloat(label)) && (
            <p className="text-xs text-blue-600 font-medium">Break-Even Point</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Format Y-axis
  const formatYAxis = (value) => {
    if (value >= 1000 || value <= -1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value}`;
  };

  // Time decay colors
  const timeDecayColors = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd'];

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="glass-effect border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center">
                <BarChart3 className="h-6 w-6 mr-2 text-indigo-600" />
                P/L Chart Builder
              </CardTitle>
              <CardDescription>
                Visualize profit/loss scenarios for single or multi-leg option strategies
              </CardDescription>
            </div>
            {stockPrice && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Current Price</p>
                <p className="text-2xl font-bold text-indigo-600">${stockPrice?.toFixed(2)}</p>
              </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Symbol Input */}
          <div className="flex gap-3">
            <div className="flex-1">
              <Label>Stock Symbol</Label>
              <Input
                placeholder="Enter symbol (e.g., AAPL)"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && fetchStockData()}
                className="text-lg"
              />
            </div>
            <div className="flex items-end">
              <Button 
                onClick={fetchStockData} 
                className="btn-primary"
                disabled={loading || !symbol.trim()}
                data-testid="load-data-btn"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Loading...' : 'Load Data'}
              </Button>
              <Button
                onClick={() => symbol && setIsChartOpen(true)}
                disabled={!symbol.trim()}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
                title="View Live Chart"
                data-testid="view-chart-btn"
              >
                <CandlestickChart className="h-4 w-4 mr-2" />
                Chart
              </Button>
            </div>
          </div>

          {/* Expiration Selector */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Expiration Date</Label>
              {expirations.length > 0 ? (
                <Select value={selectedExpiration} onValueChange={(value) => {
                  setSelectedExpiration(value);
                  const expDate = new Date(value);
                  const today = new Date();
                  const diffDays = Math.ceil(Math.abs(expDate - today) / (1000 * 60 * 60 * 24));
                  setDaysToExpiration(diffDays);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select expiration" />
                  </SelectTrigger>
                  <SelectContent>
                    {expirations.map(exp => (
                      <SelectItem key={exp} value={exp}>{exp}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input 
                  type="date"
                  value={selectedExpiration}
                  onChange={(e) => {
                    setSelectedExpiration(e.target.value);
                    const expDate = new Date(e.target.value);
                    const today = new Date();
                    const diffDays = Math.ceil(Math.abs(expDate - today) / (1000 * 60 * 60 * 24));
                    setDaysToExpiration(diffDays);
                  }}
                  placeholder="Select expiration date"
                />
              )}
            </div>
            <div>
              <Label>Days to Expiration</Label>
              <Input 
                type="number" 
                value={daysToExpiration} 
                onChange={(e) => setDaysToExpiration(parseInt(e.target.value) || 0)}
                min="0"
              />
            </div>
          </div>

          {/* Strategy Templates */}
          <div>
            <Label className="mb-2 block">Quick Strategy Templates</Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STRATEGY_TEMPLATES).map(([key, template]) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => applyTemplate(key)}
                  className="text-xs"
                >
                  {template.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Option Legs */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-lg font-semibold">Option Legs</Label>
                <p className="text-xs text-gray-500 mt-1">Premiums are auto-fetched from market when you generate the chart</p>
              </div>
              <Button variant="outline" size="sm" onClick={addLeg}>
                <Plus className="h-4 w-4 mr-1" />
                Add Leg
              </Button>
            </div>

            {legs.map((leg, index) => (
              <div key={leg.id} className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-3">
                  <Badge variant={leg.position_type === 'buy' ? 'default' : 'secondary'}>
                    Leg {index + 1}: {leg.position_type === 'buy' ? 'BUY' : 'SELL'} {leg.option_type?.toUpperCase()}
                  </Badge>
                  {legs.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeLeg(leg.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select 
                      value={leg.option_type} 
                      onValueChange={(v) => updateLeg(leg.id, 'option_type', v)}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="call">Call</SelectItem>
                        <SelectItem value="put">Put</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Buy/Sell</Label>
                    <Select 
                      value={leg.position_type} 
                      onValueChange={(v) => updateLeg(leg.id, 'position_type', v)}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buy">
                          <span className="flex items-center">
                            <TrendingUp className="h-3 w-3 mr-1 text-green-600" /> Buy
                          </span>
                        </SelectItem>
                        <SelectItem value="sell">
                          <span className="flex items-center">
                            <TrendingDown className="h-3 w-3 mr-1 text-red-600" /> Sell
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Strike Price</Label>
                    <Input
                      type="number"
                      placeholder="Strike"
                      value={leg.strike}
                      onChange={(e) => updateLeg(leg.id, 'strike', e.target.value)}
                      className="bg-white"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Premium <span className="text-gray-400">(auto-fetch)</span></Label>
                    <div className="flex gap-1">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Auto"
                        value={leg.premium}
                        onChange={(e) => updateLeg(leg.id, 'premium', e.target.value)}
                        className={`bg-white ${!leg.premium ? 'border-dashed' : ''}`}
                      />
                      <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={() => fetchPremium(index)}
                        disabled={leg.loading || !leg.strike}
                        title="Fetch premium from market"
                      >
                        {leg.loading ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <DollarSign className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={leg.quantity}
                      onChange={(e) => updateLeg(leg.id, 'quantity', e.target.value)}
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Calculate Button */}
          <Button 
            onClick={calculateChart} 
            disabled={loading}
            className="w-full btn-primary py-6 text-lg"
          >
            {loading ? (
              <>
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="h-5 w-5 mr-2" />
                Generate P/L Chart
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Chart Results */}
      {chartData && (
        <>
          {/* Metrics Card */}
          <Card className="glass-effect border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Target className="h-5 w-5 mr-2 text-indigo-600" />
                Strategy Metrics
              </CardTitle>
              <CardDescription>{chartData.strategy_description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-600 mb-1">Break-Even</p>
                  <p className="text-lg font-bold text-blue-600">
                    {chartData.metrics.break_evens.length > 0 
                      ? chartData.metrics.break_evens.map(be => `$${be}`).join(', ')
                      : 'N/A'
                    }
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-600 mb-1">Max Profit</p>
                  <p className="text-lg font-bold text-green-600">
                    {chartData.metrics.max_profit === 'Unlimited' 
                      ? 'Unlimited' 
                      : `$${chartData.metrics.max_profit.toLocaleString()}`
                    }
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-600 mb-1">Max Loss</p>
                  <p className="text-lg font-bold text-red-600">
                    {chartData.metrics.max_loss === 'Unlimited'
                      ? 'Unlimited'
                      : `$${Math.abs(chartData.metrics.max_loss).toLocaleString()}`
                    }
                  </p>
                </div>
                <div className={`${chartData.metrics.net_premium >= 0 ? 'bg-green-50' : 'bg-orange-50'} rounded-lg p-4 text-center`}>
                  <p className="text-xs text-gray-600 mb-1">Net Premium</p>
                  <p className={`text-lg font-bold ${chartData.metrics.net_premium >= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                    {chartData.metrics.net_premium >= 0 ? '+' : ''}${chartData.metrics.net_premium.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">{chartData.metrics.net_premium >= 0 ? 'Credit' : 'Debit'}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-600 mb-1">Total Contracts</p>
                  <p className="text-lg font-bold text-purple-600">
                    {chartData.metrics.total_contracts}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chart Card */}
          <Card className="glass-effect border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-indigo-600" />
                  P/L Visualization
                </CardTitle>
                {chartData.time_decay_data?.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Button
                      variant={activeChartView === 'expiration' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setActiveChartView('expiration')}
                    >
                      At Expiration
                    </Button>
                    <Button
                      variant={activeChartView === 'time_decay' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setActiveChartView('time_decay')}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Time Decay
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  {activeChartView === 'expiration' ? (
                    <ComposedChart data={chartData.pl_at_expiration}>
                      <defs>
                        <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3}/>
                          <stop offset="100%" stopColor="#22c55e" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="lossGradient" x1="0" y1="1" x2="0" y2="0">
                          <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3}/>
                          <stop offset="100%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="price" 
                        tickFormatter={(v) => `$${v}`}
                        stroke="#6b7280"
                        fontSize={12}
                      />
                      <YAxis 
                        tickFormatter={formatYAxis}
                        stroke="#6b7280"
                        fontSize={12}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={2} />
                      <ReferenceLine 
                        x={chartData.current_price} 
                        stroke="#6366f1" 
                        strokeDasharray="5 5" 
                        strokeWidth={2}
                        label={{ 
                          value: `Current: $${chartData.current_price}`, 
                          position: 'top',
                          fill: '#6366f1',
                          fontSize: 12
                        }}
                      />
                      {chartData.metrics.break_evens.map((be, i) => (
                        <ReferenceLine 
                          key={i}
                          x={be} 
                          stroke="#f59e0b" 
                          strokeDasharray="3 3"
                          label={{ 
                            value: `BE: $${be}`, 
                            position: 'insideTopRight',
                            fill: '#f59e0b',
                            fontSize: 11
                          }}
                        />
                      ))}
                      <Area
                        type="monotone"
                        dataKey="pl"
                        stroke="none"
                        fill="url(#profitGradient)"
                        fillOpacity={1}
                        isAnimationActive={false}
                        activeDot={false}
                        legendType="none"
                        name=""
                        style={{ pointerEvents: 'none' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="pl" 
                        stroke="#6366f1" 
                        strokeWidth={3}
                        dot={false}
                        activeDot={{ r: 6, fill: '#6366f1' }}
                        name="P/L at Expiration"
                      />
                    </ComposedChart>
                  ) : (
                    <LineChart>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="price" 
                        tickFormatter={(v) => `$${v}`}
                        stroke="#6b7280"
                        fontSize={12}
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        allowDuplicatedCategory={false}
                      />
                      <YAxis 
                        tickFormatter={formatYAxis}
                        stroke="#6b7280"
                        fontSize={12}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={2} />
                      <ReferenceLine 
                        x={chartData.current_price} 
                        stroke="#6366f1" 
                        strokeDasharray="5 5" 
                        strokeWidth={2}
                      />
                      {chartData.time_decay_data.map((timeData, index) => (
                        <Line
                          key={index}
                          data={timeData.data}
                          type="monotone"
                          dataKey="pl"
                          stroke={timeDecayColors[index % timeDecayColors.length]}
                          strokeWidth={index === chartData.time_decay_data.length - 1 ? 3 : 2}
                          dot={false}
                          name={timeData.label}
                          strokeDasharray={index === chartData.time_decay_data.length - 1 ? "0" : "5 5"}
                        />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>

              {/* Chart Legend */}
              <div className="mt-4 flex flex-wrap gap-4 justify-center text-sm">
                <div className="flex items-center">
                  <div className="w-4 h-1 bg-indigo-500 mr-2"></div>
                  <span>P/L Line</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-1 bg-indigo-500 mr-2 border-dashed border-t-2 border-indigo-500"></div>
                  <span>Current Price</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-1 bg-amber-500 mr-2 border-dashed border-t-2 border-amber-500"></div>
                  <span>Break-Even</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-1 bg-gray-400 mr-2"></div>
                  <span>Zero P/L</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">How to Read This Chart</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li>The X-axis shows possible stock prices at expiration</li>
                    <li>The Y-axis shows your profit or loss in dollars</li>
                    <li>Above the zero line = profit, below = loss</li>
                    <li>Break-even points are where the line crosses zero</li>
                    <li>Time decay view shows how P/L changes as expiration approaches</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Trading Chart Modal */}
      <TradingChartModal
        isOpen={isChartOpen}
        onClose={() => setIsChartOpen(false)}
        symbol={symbol}
      />
    </div>
  );
};

export default PLChart;
